import React, { useEffect, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { devBypass, signOut, startSignIn, externalEnabled, system, consumeAutoLogin } from "./lib/auth.js";
import { api, devIdentity } from "./lib/api.js";
import { usePath, navigate, rememberInviteKey, storedInviteKey } from "./lib/router.js";
import Invite from "./screens/Invite.jsx";
import AdminShell from "./screens/admin/AdminShell.jsx";
import Agent from "./screens/Agent.jsx";
import Done from "./screens/Done.jsx";
import NotInvited from "./screens/NotInvited.jsx";
import wordmark from "./assets/lipari-wordmark-macadamia.svg";
import wordmarkDark from "./assets/lipari-wordmark-zaatar.svg";

function Shell({ who, wide, children }) {
  return (
    <>
      <header className="topbar">
        <a className="brand" href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }} aria-label="Lipari Food Show, home">
          <img src={wordmark} alt="Lipari" className="wordmark" /><span className="divider" aria-hidden="true" /><span className="sub">FOOD SHOW</span>
        </a>
        <div className="who">{who}{who && !devBypass && system === "external" && <span className="dev"> · customer</span>}{devBypass && <DevSwitch />}{who && !devBypass && <button className="ghost signout" onClick={signOut}>Sign out</button>}</div>
      </header>
      {children !== undefined && <main className={wide ? "wide" : ""}>{children}</main>}
    </>
  );
}

function DevSwitch() {
  // Local only: cycle the pretend identity the API sees under the dev bypass.
  const current = devIdentity.kind === "external" ? "external" : devIdentity.roles;
  const next = { rep: ["admin", "internal"], admin: ["granted", "external"], external: ["rep", "internal"] }[current] ?? ["rep", "internal"];
  const set = () => { devIdentity.set(devIdentity.user, next[0], next[1]); window.location.reload(); };
  return <span className="dev"> · dev as <button className="chip on" onClick={set}>{current}</button></span>;
}

function SignIn() {
  // After switching identity system the page reloads; finish the sign-in the person asked for.
  useEffect(() => { if (consumeAutoLogin()) startSignIn(system); }, []);
  return (
    <Shell who="">
      <img src={wordmarkDark} alt="Lipari" className="hero-wordmark" />
      <p className="eyebrow">Food Show</p>
      <h1>Sign in to continue</h1>
      <p className="lead">Lipari employees sign in with their work account. Registration works from the link in your invitation.{!externalEnabled && " Customers and partners register through the Lipari customer portal, not here."}</p>
      <button className="primary" onClick={() => startSignIn("internal")}>Sign in with Lipari</button>
      {externalEnabled && (
        <>
          <h2> </h2>
          <button className="add" onClick={() => startSignIn("external")}>Guest or partner sign-in</button>
          <p className="help">For guest speakers, consultants and the travel agent. Create an account with the email address your invitation was sent to.</p>
        </>
      )}
    </Shell>
  );
}

function NotConfigured() {
  return (
    <Shell who="">
      <p className="eyebrow">Not open yet</p>
      <h1>Registration isn't switched on</h1>
      <p className="lead">Sign-in has not been configured for this environment. If you expected to register here, tell the events team.</p>
    </Shell>
  );
}

// Signed in. Route on the path: /invite/<key>, /admin, or home.
function Authed({ account }) {
  const path = usePath();
  const who = account?.name ?? account?.username ?? "";
  const [me, setMe] = useState(null);
  const [meError, setMeError] = useState(null);
  const [mine, setMine] = useState(undefined); // undefined = not asked yet
  const loadMe = () => { setMeError(null); api("/me").then(setMe).catch((err) => setMeError(err.message || "Could not reach the server")); };
  useEffect(loadMe, []);
  if (meError) {
    return (
      <Shell who={who}>
        <p className="eyebrow">Not available</p>
        <h1>Can't reach the registration service</h1>
        <p className="lead">Your sign-in is fine; the service did not answer ({meError}). Try again in a moment.</p>
        <button className="primary" onClick={loadMe}>Try again</button>
      </Shell>
    );
  }
  const isAdmin = me?.roles?.includes("admin");
  const isAgent = me?.roles?.includes("agent");

  const inviteMatch = path.match(/^\/invite\/([a-z0-9]+)/i);
  if (inviteMatch) {
    return <Shell who={who}><Invite inviteKey={inviteMatch[1].toLowerCase()} account={account} /></Shell>;
  }
  if (path.startsWith("/agent")) {
    if (!me) return <Shell who={who}><p className="lead">Loading…</p></Shell>;
    const allowed = isAgent || isAdmin || me.roles?.includes("view-only");
    if (!allowed) return <Shell who={who}><NotInvited reason="Your account doesn't have access to the agent screen." /></Shell>;
    return <><Shell who={who} wide /><Agent canBook={isAgent || isAdmin} /></>;
  }
  if (path.startsWith("/admin")) {
    if (!me) return <Shell who={who}><p className="lead">Loading…</p></Shell>;
    if (!(isAdmin || me.roles?.includes("view-only"))) return <Shell who={who}><NotInvited reason="Your account doesn't have access to the events-team back office." /></Shell>;
    return <AdminShell path={path} who={who} isAdmin={isAdmin} DevSwitch={DevSwitch} />;
  }
  // Home: a remembered invite key goes back to its link; admins go to the back office.
  const remembered = storedInviteKey();
  if (remembered) { navigate(`/invite/${remembered}`); return null; }
  if (!me) return <Shell who={who}><p className="lead">Loading…</p></Shell>;
  if (isAdmin) { navigate("/admin"); return null; }
  if (isAgent) { navigate("/agent"); return null; }
  if (mine === undefined) { api("/registrations/mine").then(r => setMine(r.registration)).catch(() => setMine(null)); return <Shell who={who}><p className="lead">Loading…</p></Shell>; }
  if (mine) return <Shell who={who}><Done registration={mine} /></Shell>;
  return <Shell who={who}><NotInvited /></Shell>;
}

export default function App({ account: bypassAccount, notConfigured }) {
  // Capture the invite key before any sign-in redirect can lose it.
  const m = window.location.pathname.match(/^\/invite\/([a-z0-9]+)/i);
  if (m) rememberInviteKey(m[1].toLowerCase());

  if (notConfigured) return <NotConfigured />;
  if (devBypass) return <Authed account={bypassAccount} />;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isAuthed = useIsAuthenticated();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { accounts } = useMsal();
  return isAuthed ? <Authed account={accounts[0]} /> : <SignIn />;
}
